import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [token, setToken] = useState(localStorage.getItem('amom_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('amom_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setOrg(null);
    setPermissions([]);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      setOrg(response.data.org);
      setPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, org: orgData } = response.data;
    localStorage.setItem('amom_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setOrg(orgData);
    return userData;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { access_token, user: userData, org: orgData } = response.data;
    localStorage.setItem('amom_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setOrg(orgData);
    return userData;
  };

  // `logout` is defined above with `useCallback`

  // Handle SSO callback
  const handleSSOCallback = (ssoToken, provider) => {
    localStorage.setItem('amom_token', ssoToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${ssoToken}`;
    setToken(ssoToken);
    // User data will be fetched by the useEffect
  };

  // Initiate SSO login
  const initSSO = async (provider, orgDomain) => {
    const response = await axios.get(`${API}/auth/sso/${provider}/init?org_domain=${orgDomain}`);
    return response.data;
  };

  const hasPermission = (permission) => {
    if (user?.role === 'superadmin') return true;
    return permissions.includes(permission);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const hasModuleAccess = (module) => {
    if (user?.role === 'superadmin' || user?.role === 'platform_admin') return true;
    return org?.enabled_modules?.includes(module) || false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      org, 
      token, 
      loading, 
      permissions,
      login, 
      register, 
      logout,
      hasPermission,
      hasRole,
      hasModuleAccess,
      handleSSOCallback,
      initSSO
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
